import { create } from "zustand";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/utils/supabase/client";

/**
 * Enhanced History store for smart undo/redo functionality
 * Tracks detailed action information for AI-powered undo/redo
 */

// Helper function to persist widget deletions to database
const persistWidgetDeletions = async (actions) => {
  if (!actions || actions.length === 0) return;

  const deletionActions = actions.filter(
    (action) => action.type === "WIDGET_DELETE"
  );

  if (deletionActions.length === 0) return;

  for (const action of deletionActions) {
    const deletedWidget =
      action.payload.deletedWidget || action.payload.widgetId;
    const widgetId =
      typeof deletedWidget === "string" ? deletedWidget : deletedWidget?.id;

    if (widgetId) {
      try {
        const { error } = await supabase
          .from("space_widget_drafts")
          .delete()
          .eq("id", widgetId);
      } catch (err) {
        console.error(
          `❌ [HistoryStore] Error persisting deletion of widget ${widgetId}:`,
          err
        );
      }
    }
  }
};

// Helper to create action descriptions
const getActionDescription = (action) => {
  switch (action.type) {
    case "WIDGET_ADD":
      const addedWidget = action.payload.addedWidget;
      const addedWidgetType =
        addedWidget?.widget_type?.name ||
        addedWidget?.widget_type?.type ||
        "widget";
      return `Added ${addedWidgetType}`;
    case "WIDGET_DELETE":
      const deletedWidget = action.payload.deletedWidget;
      const deletedWidgetType =
        deletedWidget?.widget_type?.name ||
        deletedWidget?.widget_type?.type ||
        "widget";
      return `Deleted ${deletedWidgetType}`;
    case "WIDGET_UPDATE":
      const updates = Object.keys(action.payload.changes || {}).join(", ");
      return `Updated widget: ${updates}`;
    case "CONTENT_CHANGE":
      return "Changed text content";
    case "CONFIG_CHANGE":
      // Check if we have specific config change information
      const configChanges = action.payload.changes;
      if (configChanges && typeof configChanges === "object") {
        const changeKeys = Object.keys(configChanges);
        if (changeKeys.includes("config")) {
          const configKeys = Object.keys(configChanges.config || {});
          if (configKeys.length > 0) {
            return `Changed settings: ${configKeys.join(", ")}`;
          }
        }
      }
      return "Changed widget settings";
    case "CONFIG_CHANGE_BATCH":
      const widgetCount = Object.keys(action.payload.widgets || {}).length;
      return `Changed settings (${widgetCount} widgets)`;
    case "LAYOUT_CHANGE":
      // Check if we have specific layout change information
      const layoutChanges = action.payload.changes;
      if (layoutChanges && typeof layoutChanges === "object") {
        const changeKeys = Object.keys(layoutChanges);
        if (changeKeys.includes("layout")) {
          const layoutKeys = Object.keys(layoutChanges.layout || {});
          if (layoutKeys.length > 0) {
            const layoutTypes = [];
            if (
              layoutKeys.some((k) => ["columns", "columnHeight"].includes(k))
            ) {
              layoutTypes.push("size");
            }
            if (
              layoutKeys.some((k) => ["position", "startColumn"].includes(k))
            ) {
              layoutTypes.push("position");
            }
            if (layoutKeys.some((k) => ["settings"].includes(k))) {
              layoutTypes.push("style");
            }
            if (layoutTypes.length > 0) {
              return `Changed widget ${layoutTypes.join(" & ")}`;
            }
          }
        }
      }
      return "Changed widget layout";
    case "LAYOUT_CHANGE_BATCH":
      const layoutWidgetCount = Object.keys(
        action.payload.widgets || {}
      ).length;
      return `Changed layout (${layoutWidgetCount} widgets)`;
    case "WIDGET_RESIZE":
      const widgetType = action.payload?.widgetType || "widget";
      const fromSize = action.payload?.fromSize || "";
      const toSize = action.payload?.toSize || "";
      return fromSize && toSize
        ? `Resized ${widgetType} from ${fromSize} to ${toSize}`
        : `Resized ${widgetType}`;
    case "THEME_CHANGE":
      // Check if we have specific theme change information
      const themeChanges = action.payload?.spaceDataChanges;
      if (themeChanges?.theme) {
        const theme = themeChanges.theme;
        if (theme.background?.type === "image") {
          return "Changed background image";
        } else if (theme.background?.type === "color") {
          return "Changed background color";
        } else if (theme.fonts) {
          return "Changed typography";
        } else if (theme.colors) {
          return "Changed color scheme";
        }
      }
      return "Changed theme settings";
    case "SPACE_DATA_CHANGE":
      // Check if we have specific space data change information
      const changeDescription = action.payload?.changeDescription;
      if (changeDescription) {
        return changeDescription;
      }
      return "Updated space settings";
    case "MOBILE_LAYOUT_FIX":
      return "Fixed mobile layout";
    case "UPDATE_WIDGET":
      return "Updated widget positions";
    case "INITIAL_STATE":
      return "Initial space state";
    case "UNDO_ACTION":
      return "Undo operation";
    case "REDO_ACTION":
      return "Redo operation";
    default:
      return action.type;
  }
};

// Create the history store slice for the main store
export const createHistoryStore = (set, get) => ({
  actions: [],
  currentIndex: -1,
  maxActions: 50, // Limit to 50 actions total

  // Enhanced action with metadata
  addAction: (action) => {
    const { actions, currentIndex, maxActions } = get();

    // Create enhanced action entry
    const enhancedAction = {
      id: uuidv4(),
      timestamp: new Date().toISOString(),
      type: action.type,
      description: getActionDescription(action),
      payload: action.payload,
      previousState: action.previousState,
      newState: action.newState,
      metadata: {
        ...action.metadata,
        sessionId: window.sessionStorage.getItem("sessionId") || uuidv4(),
      },
    };

    // Remove future actions if we're not at the end
    let newActions = actions.slice(0, currentIndex + 1);
    newActions.push(enhancedAction);

    // Enforce the maximum number of actions
    if (newActions.length > maxActions) {
      const actionsToRemove = newActions.slice(
        0,
        newActions.length - maxActions
      );

      // Persist any widget deletions that are being removed from history
      try {
        persistWidgetDeletions(actionsToRemove);
      } catch (error) {
        console.error("Failed to persist widget deletions:", error);
      }

      newActions = newActions.slice(-maxActions);
    }

    set({
      actions: newActions,
      currentIndex: newActions.length - 1,
    });
  },

  // Alias for addAction to maintain compatibility
  pushAction: (action) => {
    return get().addAction(action);
  },

  // Get recent actions for AI context
  getRecentActions: (limit = 15) => {
    const { actions, currentIndex } = get();
    const startIndex = Math.max(0, currentIndex - limit + 1);
    const recentActions = actions.slice(startIndex, currentIndex + 1);

    return recentActions;
  },

  // Traditional undo (kept for fallback)
  undo: (steps = 1) => {
    const { currentIndex, actions } = get();
    const targetIndex = Math.max(-1, currentIndex - steps);

    if (targetIndex !== currentIndex && targetIndex >= -1) {
      set({ currentIndex: targetIndex });
      return targetIndex >= 0 ? actions[targetIndex] : null;
    }
    return null;
  },

  // Traditional redo (kept for fallback)
  redo: (steps = 1) => {
    const { actions, currentIndex } = get();
    const targetIndex = Math.min(actions.length - 1, currentIndex + steps);

    if (targetIndex !== currentIndex && targetIndex < actions.length) {
      set({ currentIndex: targetIndex });
      return actions[targetIndex];
    }
    return null;
  },

  // Smart undo - returns action for processing
  smartUndo: async () => {
    const { getRecentActions } = get();

    const actionHistory = getRecentActions();

    return {
      action: "undo",
      actionHistory,
    };
  },

  // Smart redo - returns action for processing
  smartRedo: async () => {
    const { getRecentActions, actions, currentIndex } = get();

    // Include future actions for redo context
    const futureActions = actions.slice(currentIndex + 1, currentIndex + 6);
    const actionHistory = getRecentActions();

    return {
      action: "redo",
      actionHistory,
      futureActions,
    };
  },

  // Apply state after smart undo/redo
  applySmartAction: (targetState, actionsTaken) => {
    const { actions, currentIndex } = get();

    // Find how many actions were undone/redone
    const actionCount = actionsTaken?.length || 1;

    // Update current index based on the action
    if (targetState) {
      // For now, we'll adjust the index based on actionsTaken
      // This can be made smarter based on the actual actions
      const newIndex = Math.max(-1, currentIndex - actionCount);

      set({
        currentIndex: newIndex,
      });
    }
  },

  canUndo: () => {
    const { currentIndex } = get();
    return currentIndex >= 0;
  },

  canRedo: () => {
    const { actions, currentIndex } = get();
    return currentIndex < actions.length - 1;
  },

  getCurrentAction: () => {
    const { actions, currentIndex } = get();
    return actions[currentIndex] || null;
  },

  getActionAt: (index) => {
    const { actions } = get();
    return actions[index] || null;
  },

  // Clear history and start fresh
  clearHistory: () => {
    set((state) => {
      // Persist any widget deletions before clearing
      try {
        persistWidgetDeletions(state.actions);
      } catch (error) {
        console.error(
          "Failed to persist widget deletions during clear:",
          error
        );
      }

      return {
        actions: [],
        currentIndex: -1,
      };
    });
  },

  // Persist all pending widget deletions (useful before publishing/saving)
  persistPendingDeletions: async () => {
    const { actions } = get();
    await persistWidgetDeletions(actions);
  },

  // Debug helper
  getHistoryState: () => {
    const { actions, currentIndex } = get();
    return {
      totalActions: actions.length,
      currentIndex,
      maxActions: 50,
      canUndo: currentIndex >= 0,
      canRedo: currentIndex < actions.length - 1,
      recentActions: get().getRecentActions(5),
    };
  },
});

// Standalone hook for components that need history functionality
const useHistoryStore = create((set, get) => ({
  ...createHistoryStore(set, get),
}));

export default useHistoryStore;
